// @ts-check

import { useMutation, useQuery } from "@tanstack/react-query"
import { atom, useAtom, useSetAtom } from "jotai"
import { useSelector } from "react-redux"
import { ChatReportQueryOptions, ChatReportMutationOptions } from "./query"
import { useActiveChatSessionId, useCreateChatSession, useIsAwaitingNewSession } from "../sessions"
import { StreamEvents } from "../api"
import { ActiveStreamsAtomActions, activeStreamsAtom, useGetChatSessionStream, useStreamMutation } from "../streams"
import { clearReviewProgress } from "../canvas/reviewProgress"
import { ReportStreamContentChunkSchema, ReportStreamStructuredContentChunkSchema } from "../schema"
import { useGetLatestUserToken, OPEN_REPORT_PANEL_EVENT } from "../utils"
import useSharedEffect from "@/hooks/useSharedEffect"

const pendingReportAtom = atom(/**@type{ChatReportInput | null}*/(null))

function usePendingReport() {
  return useAtom(pendingReportAtom)
}

function useActiveChatSessionReports() {
  // @ts-ignore
  const { user } = useSelector((state) => state.app.datastate)
  const [activeSessionId] = useActiveChatSessionId()

  return useQuery(
    ChatReportQueryOptions.CHAT_SESSION_REPORTS({
      sessionId: activeSessionId,
      userToken: user?.userToken,
    })
  )
}

const PendingReportEffectSymbol = Symbol("PendingReportEffect")
function useGenerateReport() {
  const { user } = useSelector((state) => state.app.datastate)
  const [activeChatSessionId] = useActiveChatSessionId()
  const setActiveStreams = useSetAtom(activeStreamsAtom)
  const getUserToken = useGetLatestUserToken()

  const createChatSessionMutation = useCreateChatSession()

  const [pendingReport, setPendingReport] = useAtom(pendingReportAtom)
  const [isAwaitingNewSession, setIsAwaitingNewSession] = useIsAwaitingNewSession()

  useSharedEffect(PendingReportEffectSymbol, () => {
    if(isAwaitingNewSession) return
    if(pendingReport == null) return
    if(activeChatSessionId == null) return

    generateReportMutation.mutate({ sessionId: activeChatSessionId, input: pendingReport })
    setPendingReport(null)
  }, [pendingReport, isAwaitingNewSession, activeChatSessionId])

  /** @param {import("../api").StreamEventsPayload} payload */
  function handleStreamEvent(payload) {
    switch(payload.event) {
      case StreamEvents.STREAM_STARTED: {
        break;
      }
      case StreamEvents.STREAM_CONTENT: {
        let chunk
        try {
          chunk = ReportStreamContentChunkSchema.parse(payload.data.chunk)
        } catch (error) {
          console.warn("Failed to parse report stream content event data", error)
          return
        }

        setActiveStreams(
          ActiveStreamsAtomActions.APPEND_RESPONSE_CHUNK(
            payload.data.sessionId,
            "content",
            chunk.data.content
          )
        )
        break;
      }
      case StreamEvents.STREAM_STRUCTURED_CONTENT: {
        let chunk
        try {
          chunk = ReportStreamStructuredContentChunkSchema.parse(payload.data.chunk)
        } catch (error) {
          console.warn("Failed to parse report stream structured content event data", error)
          return
        }

        if(chunk.data.content.event === "error") {
          console.error(chunk.data.content.error)
          throw new Error(chunk.data.content.message)
        }

        setActiveStreams(
          ActiveStreamsAtomActions.APPEND_RESPONSE_CHUNK(
            payload.data.sessionId,
            "structured-content",
            {
              event: chunk.data.content.event,
              data: chunk.data.content,
            }
          )
        )
        break;
      }
      case StreamEvents.STREAM_ENDED: {
        break;
      }
      default:
        break;
    }
  }

  const generateReportMutationOptions = ChatReportMutationOptions.GENERATE_REPORT({
    getUserToken,
    onStreamEvent: handleStreamEvent,
  })

  const { mutate: getSessionStream } = useGetChatSessionStream()

  const generateReportMutation = useStreamMutation(generateReportMutationOptions, {
    mutationFn: async (variables, context) => {
      if(variables.sessionId == null) {
        setPendingReport(variables.input)
        setIsAwaitingNewSession(true)

        await createChatSessionMutation.mutateAsync().catch(error => {
          // TODO: Maybe revert report to inputs to be resubmitted
          setPendingReport(null)
          throw error
        }).finally(() => {
          setIsAwaitingNewSession(false)
        })

        const abortController = new AbortController()
        abortController.abort()
        abortController.signal.throwIfAborted()
      }

      return generateReportMutationOptions.mutationFn(variables, context)
    },
    onMutate(variables) {
      if(variables.sessionId == null) return

      clearReviewProgress(variables.sessionId)
      setActiveStreams(
        ActiveStreamsAtomActions.SETUP_REPORT_STREAM(variables.sessionId, {
          modality: variables.input.modality,
          patientInfo: variables.input.patientInfo,
          image: variables.input.image,
        })
      )

      document.dispatchEvent(new CustomEvent(OPEN_REPORT_PANEL_EVENT, {
        detail: {
          report: {
            type: "stream",
            sessionId: variables.sessionId,
          }
        }
      }))
    },
    onError: (error, variables, mutationResult, context) => {
      if(error.name === "AbortError") {
        generateReportMutation.reset()
        return
      }
    },
    onSettled: (data, error, variables) => {
      setActiveStreams(ActiveStreamsAtomActions.DELETE_STREAM(variables.sessionId))
    }
  }, {
    getSessionStream,
    sessionId: activeChatSessionId
  })

  return generateReportMutation
}

function useUpdateReport() {
  const { user } = useSelector(state => state.app.datastate)
  const [activeChatSessionId] = useActiveChatSessionId()

  return useMutation(ChatReportMutationOptions.UPDATE_REPORT({
    sessionId: activeChatSessionId,
    userToken: user?.userToken,
  }))
}

export * from "./query"
export {
  pendingReportAtom,

  usePendingReport,

  useActiveChatSessionReports,
  useGenerateReport,
  useUpdateReport,
}
